import { security } from '@beelday/common'

/**
 * How long to wait before each new try at a stream that keeps failing: quick
 * at first, for the blip of a phone waking up, then twice a minute.
 */
export const REOPEN_DELAYS_MS = [1_000, 2_000, 5_000, 10_000, 30_000]

export const reopenDelay = (attempt: number): number =>
	REOPEN_DELAYS_MS[Math.min(Math.max(attempt, 0), REOPEN_DELAYS_MS.length - 1)]

/**
 * Away for longer than this, a page has missed events even if its stream
 * still looks open: a phone's browser stops a page in another app's
 * foreground, and the events of that time are gone with the connection.
 */
export const LONG_ABSENCE_MS = 10_000

/** A login closer than this to its end is refreshed before a stream opens. */
export const LOGIN_MARGIN_MS = 60_000

export type StreamHealth = {
	/** The browser reports the stream open. */
	open: boolean
	/** When the stream last showed it is alive: opened, or a server beat. */
	lastSignOfLifeAt: number
}

/**
 * The server beats every 20 seconds on every stream; a stream that has said
 * nothing for longer than `timeoutMs` is dead, however open it looks.
 */
export const isSilent = (
	health: StreamHealth,
	now: number,
	timeoutMs: number
): boolean => now - health.lastSignOfLifeAt > timeoutMs

/**
 * Back on the page: reopen - and so get the room's state afresh - unless the
 * stream is open, alive and the page was away only briefly.
 */
export const shouldReopenOnReturn = ({
	health,
	now,
	timeoutMs,
	awayMs,
}: {
	health: StreamHealth
	now: number
	timeoutMs: number
	awayMs: number
}): boolean =>
	!health.open || isSilent(health, now, timeoutMs) || awayMs > LONG_ABSENCE_MS

/**
 * A stream sends the login along as a cookie, so a login that ran out while
 * the phone was in the background is refreshed first: refused, the stream
 * would be closed for good.
 */
export const needsFreshLogin = (
	tokenState: security.TokenState,
	now: number
): boolean =>
	tokenState.type === 'EXPIRED_TOKENS' ||
	(tokenState.type === 'VALID_TOKENS' &&
		tokenState.expirationDate - now < LOGIN_MARGIN_MS)
